import { useCallback } from 'react';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { Transaction } from './types';

/**
 * Filling in the url templates the server handed this page, and putting the site's base
 * url back on the front.
 *
 * Both rules live here rather than at five call sites, because getting either wrong is
 * silent:
 *
 * The placeholder spelling. `:id`, `:hash` and `:orderNumber` are
 * ManageAutoRenewalPagePropsBuilder's constants; a component that types one of them by
 * hand and gets it wrong renders a link with the placeholder still in it, which
 * TypeScript is perfectly happy with.
 *
 * And `buildUrl`. These templates arrive relative to the site root, like every other url
 * a component is handed, so AU's /au/ has to go back on — a link rendered without it
 * leaves the site's base directory and 404s on exactly one storefront.
 */
export default function useAutoRenewalUrls() {
  const { buildUrl } = useUrlGenerator();

  return {
    /** The page that renews at the current rate, or takes up an alternate-title offer. */
    confirmUrl: useCallback(
      (template: string, transaction: Transaction) =>
        buildUrl(
          template
            .replace(':id', encodeURIComponent(String(transaction.id)))
            .replace(':hash', encodeURIComponent(transaction.hash))
        ),
      // `buildUrl` is derived from the site, which does not change within a page.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    ),

    /** The editor for the card a subscription renews against. */
    updatePaymentUrl: useCallback(
      (template: string, paymentProfileId: number) =>
        buildUrl(template.replace(':id', encodeURIComponent(String(paymentProfileId)))),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    ),

    /** One renewal's receipt on the legacy customer portal. */
    receiptUrl: useCallback(
      (template: string, orderNumber: string) =>
        buildUrl(template.replace(':orderNumber', encodeURIComponent(orderNumber))),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    ),
  };
}
